/**
 * SAT MASTER · Kanonische Salespage · /sat-master
 * Verbindlicher Inhalt gemäss FREIGABE · BUILD BLOCK 6 · Abschnitt 2.
 *
 * Hinweis 2026-02-25: Feature-Flag-Gate entfernt — die Salespage ist ein
 * öffentlicher Verkaufsfunnel und muss für potenzielle Kunden immer les-
 * und scrollbar sein. Sichtbarkeit wird nicht mehr per Flag gesteuert.
 */
import React from "react";
import { Helmet } from "react-helmet-async";
import {
  S, Section, GoldRule, PrimaryCta, TwoColumn, ChecklistList, Accordion, FooterDisclaimer,
} from "../components/salespage/SalesSections";

export default function SatMasterSalespage() {
  return (
    <div style={{ background: S.midnight, color: S.ivory }} data-testid="page-sat-master">
      <Helmet>
        <title>SAT MASTER · Die Spielregeln des Lebens</title>
        <meta name="description" content="24 Wochen für Menschen, die sich selbst wieder spüren, verstehen und im Leben neu führen wollen. Ein klar geführter Transformationsweg mit Nenad." />
        <link rel="canonical" href="https://www.sat-akademie.com/sat-master" />
        <meta name="robots" content={window.location.hostname.includes("preview") ? "noindex,nofollow" : "index,follow"} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": "SAT MASTER · Die Spielregeln des Lebens",
            "description": "24 Wochen Transformationsweg mit sechs Phasen, Workbooks, Körper- und Gefühlspraxis, Live-Calls und Integrationsplan.",
            "provider": {
              "@type": "Organization",
              "name": "SAT Akademie",
              "url": "https://www.sat-akademie.com"
            },
            "inLanguage": "de",
            "educationalLevel": "Fortgeschritten",
          })}
        </script>
      </Helmet>

      {/* 1. HERO */}
      <Section id="hero" eyebrow="SAT MASTER">
        <h1
          className="text-4xl md:text-6xl font-serif leading-tight mb-6"
          style={{ color: S.ivory }}
          data-testid="hero-title"
        >
          Die Spielregeln des Lebens
        </h1>
        <p
          className="text-lg md:text-2xl leading-relaxed max-w-3xl mb-6"
          style={{ color: S.goldSoft }}
          data-testid="hero-tagline"
        >
          24 Wochen für Menschen, die nicht noch mehr über sich wissen, sondern
          sich selbst wieder spüren, verstehen und im Leben neu führen wollen.
        </p>
        <p
          className="text-base md:text-lg leading-relaxed max-w-3xl mb-10"
          style={{ color: S.ivoryMuted }}
          data-testid="hero-sub"
        >
          Ein klar geführter Transformationsweg mit persönlichen Videos von Nenad,
          fokussierten Workbooks, Körper- und Gefühlsübungen, wöchentlichen Live-Calls
          und konkreten Schritten in deinen Alltag.
        </p>
        <PrimaryCta
          href="/sat-master/bewerbung"
          label="Meinen Platz anfragen"
          testId="hero-cta"
        />
        <p className="mt-6 text-xs" style={{ color: "rgba(246,241,231,0.6)" }}>
          Sechs Monate · sechs Transformationsphasen · 24 Wochenreisen · Kein MASTER-Zertifikat.
        </p>
      </Section>

      {/* 1b. HAMSTERRAD → LEBENDIGKEIT (neu · verkaufspsychologisch) */}
      <Section id="hamsterrad" eyebrow="Der Wendepunkt">
        <h2
          className="text-3xl md:text-5xl font-serif leading-tight mb-8 max-w-3xl"
          style={{ color: S.ivory }}
          data-testid="hamsterrad-title"
        >
          Erfolgreich — und trotzdem <span style={{ color: S.gold }}>nicht angekommen</span>?
        </h2>
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-start">
          <div className="space-y-5 text-base md:text-lg leading-relaxed" style={{ color: S.ivoryMuted }}>
            <p>
              Von aussen läuft alles. Zahlen stimmen. Der Kalender ist voll.
              Und trotzdem: ein leises Ziehen. Ein Wissen, das du nicht mehr überhören kannst.
            </p>
            <p className="serif italic text-xl md:text-2xl" style={{ color: S.goldSoft }}>
              &bdquo;Da ist mehr. Und ich weiss, dass ich es nicht länger aufschieben kann.&ldquo;
            </p>
            <p>
              Du hast viel gelesen. Viele Retreats besucht. Viele Podcasts gehört.
              <span className="text-white"> Und du merkst: noch mehr Wissen ändert nichts mehr.</span>
              Es fehlt kein Konzept — es fehlt Verkörperung.
            </p>
            <p>
              SAT MASTER ist der ruhige, klare Weg zurück zu dir selbst. Nicht als
              Selbstoptimierung. Sondern als das, was <em>wirklich</em> zählt:
              deine Zeit, deine Beziehungen, dein Nervensystem, deine Wahrhaftigkeit.
            </p>
          </div>
          <div className="space-y-4">
            <div className="p-6 rounded-xl border" style={{ borderColor: S.line, background: S.midnightSoft }}>
              <div className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: S.goldSoft }}>Kein Rezept · kein Guru · kein Hype</div>
              <ul className="space-y-2 text-sm md:text-base" style={{ color: S.ivory }}>
                <li>→ 24 Wochen ruhige, tiefe Arbeit — statt Bootcamp-Adrenalin.</li>
                <li>→ Körper, Gefühl, Wahl, Handlung — statt Kopfsalat.</li>
                <li>→ Wöchentliche Live-Calls mit Nenad — statt anonymer Community.</li>
                <li>→ 48-Stunden-Transfer in den Alltag — statt Retreat-Blase.</li>
              </ul>
            </div>
            <div className="p-6 rounded-xl border-2" style={{ borderColor: S.gold, background: `${S.gold}0d` }}>
              <div className="text-xs tracking-[0.35em] uppercase mb-2" style={{ color: S.gold }}>Hochsensibel? Willkommen.</div>
              <p className="text-sm md:text-base leading-relaxed" style={{ color: S.ivoryMuted }}>
                SAT MASTER ist gebaut für Menschen mit feinen Antennen.
                Das, was in vielen Räumen zu viel ist, wird hier dein <span className="text-white">wichtigstes Werkzeug</span>.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* 1c. KI-ZEITALTER (neu · Relevanz jetzt) */}
      <Section id="ki-invasion" eyebrow="Warum jetzt" title="Im Zeitalter der KI wird gelebtes Mensch-Sein zum entscheidenden Unterschied">
        <div className="max-w-3xl space-y-5 text-base md:text-lg leading-relaxed" style={{ color: S.ivoryMuted }}>
          <p>
            Wissen ist frei geworden. Analysen dauern Sekunden. Fast alles, was du früher
            teuer bezahlt hast, gibt es jetzt gratis.
          </p>
          <p>
            <span className="text-white">Aber: was in dir wirkt, kann kein Modell für dich tun.</span>
            {" "}Kein Chatbot geht durch deine Trauer.
            Kein Prompt löst dein Muster.
            Keine App führt dich zurück in deinen Körper.
          </p>
          <p>
            Genau darum ist <em>jetzt</em> der Zeitpunkt für SAT MASTER: für Menschen, die
            aufgehört haben, sich retten zu lassen, und angefangen haben, sich zu <span style={{ color: S.gold }}>verkörpern</span>.
          </p>
        </div>
      </Section>

      {/* 2. AUSGANGSLAGE */}
      <Section id="context" eyebrow="Ausgangslage" title="Wenn Wissen nicht mehr weiterhilft">
        <p className="text-base md:text-lg leading-relaxed" style={{ color: S.ivoryMuted }}>
          Du hast viel gelesen, viel gearbeitet, viel gegeben. Trotzdem meldet sich innen
          eine leise Stimme: »Da ist mehr«. SAT MASTER richtet sich an Menschen, die
          nicht noch eine Methode suchen, sondern einen ruhigen, klaren Weg, um
          das, was sie längst wissen, endlich zu <em>verkörpern</em>.
        </p>
      </Section>

      {/* 3. SAT-WENDEPUNKT */}
      <Section id="turningpoint" eyebrow="SAT-Wendepunkt" title="Vom Wissen zum Handeln">
        <TwoColumn
          left={
            <p className="text-base leading-relaxed" style={{ color: S.ivoryMuted }}>
              Auf dem SAT-Weg wird Wissen nicht nur verstanden. Es wird über
              <span style={{ color: S.gold }}> Körper, Gefühl, Wahl und Handlung </span>
              in dein Leben integriert. Jede Woche baut auf der vorigen — nicht mehr Input,
              sondern mehr Klarheit.
            </p>
          }
          right={
            <ChecklistList
              items={[
                "Körperliche Anker statt gedanklicher Konzepte",
                "Gefühl als Kompass, nicht als Bedrohung",
                "Bewusste Wahl statt automatischer Reaktion",
                "Handlung im Alltag — 48-Stunden-Transfer",
              ]}
              testIdPrefix="turning"
            />
          }
        />
      </Section>

      {/* 4. FÜR WEN */}
      <Section id="fitness" eyebrow="Passung" title="Für wen SAT MASTER geeignet ist – und für wen nicht">
        <TwoColumn
          left={
            <>
              <h3 className="text-lg mb-4" style={{ color: S.gold }}>Geeignet</h3>
              <ChecklistList
                items={[
                  "Menschen mit Lebenserfahrung, die tiefer gehen wollen",
                  "Reife Erwachsene, die Verantwortung für ihre nächste Etappe übernehmen",
                  "Wer bereit ist, sich selbst zu begegnen — jenseits von Coaching-Rezepten",
                  "Wer wöchentlich Zeit reserviert und Live-Calls priorisiert",
                ]}
                testIdPrefix="fit-yes"
              />
            </>
          }
          right={
            <>
              <h3 className="text-lg mb-4" style={{ color: S.goldSoft }}>Nicht geeignet</h3>
              <ChecklistList
                items={[
                  "Akute psychische oder medizinische Krisen — bitte fachliche Behandlung suchen",
                  "Wer eine schnelle Methode oder ein Erfolgsrezept erwartet",
                  "Wer nicht bereit ist, sich Zeit im Alltag zu nehmen",
                  "Wer eine Diagnose oder Therapie sucht — SAT ist Bildung, keine Behandlung",
                ]}
                testIdPrefix="fit-no"
              />
            </>
          }
        />
      </Section>

      {/* 5. CURRICULUM */}
      <Section id="curriculum" eyebrow="Aufbau" title="Sechs Phasen · 24 Wochen · vier Starthausaufgaben">
        <p className="text-base leading-relaxed mb-8" style={{ color: S.ivoryMuted }}>
          Vor dem eigentlichen Wochenprogramm klärst du dein Feld über vier Hausaufgaben.
          Danach führt der Weg dich durch sechs Phasen — jeweils vier Wochenreisen.
        </p>
        <Accordion
          testIdPrefix="master-curriculum"
          items={[
            { title: "Starthausaufgabe 1 · Schatzbuch", body: "Dein privates Bewusstseinstagebuch. Ein Ort, an dem sich Wahrnehmung ordnen darf — geschützt und nur für dich." },
            { title: "Starthausaufgabe 2 · Gesundheits-Check", body: "Ärztlich begleiteter Gesundheits- und Labor-Check. Keine Übermittlung von Laborwerten an SAT. Keine medizinische Interpretation durch uns." },
            { title: "Starthausaufgabe 3 · Lebensmeter", body: "Würdigung der vergangenen Jahre und bewusste Gestaltung der verbleibenden Lebenszeit." },
            { title: "Starthausaufgabe 4 · Handschriftliche Biografie", body: "Über zwei bis drei verschiedene Tage niedergeschrieben — nicht digital, nicht optimiert." },
            { title: "Phase 1–6 · Die Spielregeln des Lebens", body: "24 Wochenreisen entlang der Spielregeln des Lebens und des Circle of Life. Videos, Workbooks, Körper- und Gefühlspraxis, 48-Stunden-Transfer, wöchentliche Live-Calls." },
            { title: "Survival of the Fittest", body: "Ausschliesslich als menschliche Anpassungsfähigkeit verstanden — nicht als Stärke-, Konkurrenz- oder Ranglogik." },
            { title: "Partnerschaftsmodul", body: (
              <div>
                <p className="mb-3">Drei Stadien der Beziehung werden bewusst unterschieden:</p>
                <ul className="list-disc pl-5 mb-4 space-y-1">
                  <li>Therapiepartnerschaft</li>
                  <li>Liebespartnerschaft</li>
                  <li>Lebenspartnerschaft</li>
                </ul>
                <p className="mb-3">Sowie die fünf Sprachen der Liebe:</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Lob und Anerkennung</li>
                  <li>Zweisamkeit — die Zeit nur für dich</li>
                  <li>Geschenke, die von Herzen kommen</li>
                  <li>Hilfsbereitschaft</li>
                  <li>Zärtlichkeit</li>
                </ol>
              </div>
            ) },
            { title: "HPA · Human Design (optional)", body: "Als optionaler Potenzial-Kompass. Kein Muss. Keine Deutungshoheit über deinen Weg." },
            { title: "Abschluss · Landkarte + 90-Tage-Integrationsplan", body: "Kein Zertifikat. Stattdessen: eine Landkarte deiner nächsten 90 Tage — für die reale Umsetzung." },
          ]}
        />
      </Section>

      {/* 6. LEISTUNGEN */}
      <Section id="deliverables" eyebrow="Enthaltene Leistungen" title="Was du bekommst">
        <ChecklistList
          testIdPrefix="deliverables"
          items={[
            "24 Wochenreisen mit persönlichen Videos von Nenad",
            "Workbooks, Körper- und Gefühlspraxis pro Woche",
            "Wöchentliche Live-Calls in der Kohorte",
            "48-Stunden-Transfer nach jedem Live-Call",
            "Vier Starthausaufgaben zur inneren Ausrichtung",
            "Abschlusslandkarte und 90-Tage-Integrationsplan",
          ]}
        />
      </Section>

      {/* 7. ABLAUF */}
      <Section id="flow" eyebrow="Ablauf" title="Vom Erstkontakt bis zum Abschluss">
        <ol className="space-y-4" data-testid="flow-list">
          {["Du reichst eine Passungsanfrage oder ein Orientierungsgespräch ein.",
            "Wir sichten deine Anfrage persönlich. Kein automatisches Verkaufsgespräch.",
            "Erst dann entscheidest du — und entscheiden wir — ob SAT MASTER dein nächster Schritt ist.",
            "Nach bestätigter Zulassung und Zahlung: Start der Kohorte am kommunizierten Startdatum.",
            "24 Wochen, wöchentliche Rhythmus, geschütztes Learning-Umfeld.",
            "Abschluss mit Landkarte und Integrationsplan.",
          ].map((step, i) => (
            <li
              key={step}
              className="flex gap-4 items-start"
              style={{ color: S.ivoryMuted }}
            >
              <span
                className="font-serif text-2xl"
                style={{ color: S.gold, flexShrink: 0, width: "2rem" }}
                aria-hidden
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-base leading-relaxed pt-1">{step}</span>
            </li>
          ))}
        </ol>
      </Section>

      {/* 8. ÜBER NENAD */}
      <Section id="nenad" eyebrow="Über" title="Nenad Vladovic · Gründer von SAT">
        <p className="text-base leading-relaxed max-w-3xl" style={{ color: S.ivoryMuted }}>
          Nenad Vladovic begleitet Menschen seit über zwei Jahrzehnten auf ihrem
          Transformationsweg. SAT ist aus der Verbindung von gelebter Erfahrung,
          klarer Haltung und der Frage entstanden: »Wie wird aus Wissen Verkörperung?«
        </p>
        <p className="text-sm mt-4" style={{ color: "rgba(246,241,231,0.55)" }}>
          Weitere Angaben werden nur veröffentlicht, wenn sie belegbar und freigegeben sind.
        </p>
      </Section>

      {/* 9. INVESTITION / PLATZANFRAGE */}
      <Section id="investment" eyebrow="Investition" title="Platzanfrage statt Direktkauf">
        <p className="text-base leading-relaxed" style={{ color: S.ivoryMuted }}>
          SAT MASTER wird nicht spontan gekauft. Wir sichten jede Anfrage persönlich.
          Die konkrete Investition, Ratenrahmen und Konditionen erhältst du im Gespräch
          — nach bestätigter Passung.
        </p>
        <div className="mt-8">
          <PrimaryCta
            href="/sat-master/bewerbung"
            label="Mein Orientierungsgespräch buchen"
            testId="investment-cta"
          />
        </div>
      </Section>

      {/* 10. TESTIMONIALS (nur wenn Freigabe · aktuell ausgeblendet) */}
      {/* Freigabestatus prüfen: kein Testimonial im Repo mit dokumentierter Einwilligung.
          Abschnitt wird ausgeblendet, statt platzhaltend zu erscheinen. */}

      {/* 11. FAQ */}
      <Section id="faq" eyebrow="FAQ" title="Häufige Fragen">
        <Accordion
          testIdPrefix="master-faq"
          items={[
            { title: "Wie viel Zeit brauche ich pro Woche?", body: "Zwischen 3 und 5 Stunden — Video, Workbook, Körper- und Gefühlspraxis, Live-Call. Der genaue Rhythmus liegt bei dir." },
            { title: "Muss ich in den Live-Calls sprechen?", body: "Nein. Beteiligung und Offenlegung sind jederzeit freiwillig. Du kannst zuhören, mitgehen und nur so viel teilen, wie sich gut anfühlt." },
            { title: "Kann ich eine Übung stoppen?", body: "Ja, jederzeit. Du bestimmst dein Tempo. Es gibt kein »durchhalten müssen«." },
            { title: "Ist SAT eine Therapie?", body: "Nein. SAT ist ein Transformations- und Bildungsangebot und ersetzt keine medizinische oder psychotherapeutische Behandlung." },
            { title: "Muss ich Laborwerte hochladen?", body: "Nein. SAT nimmt keine Laborwerte, Diagnosen oder Medikamentendaten entgegen und interpretiert sie nicht." },
            { title: "Wann bekomme ich Zugang?", body: "Erst nach bestätigter Zulassung und Zahlung. Ein Konto oder eine Anfrage allein erzeugt keinen Kurszugang." },
            { title: "Gibt es Raten?", body: "Ratenzahlungen werden individuell besprochen und nur angeboten, wenn technisch und vertraglich sauber konfiguriert." },
            { title: "Gibt es ein MASTER-Zertifikat?", body: "Nein. SAT MASTER schliesst mit einer Landkarte und einem 90-Tage-Integrationsplan ab — kein Zertifikat." },
            { title: "Wie lange habe ich Zugang zu den Inhalten?", body: "Die Zugangsdauer wird im Angebot festgelegt. Sie ist Teil der Vereinbarung, keine offene Verlängerung." },
            { title: "Wie werden Videos ausgeliefert?", body: "Videos werden ausschliesslich als geschützter Stream bereitgestellt. PDFs stehen nur lektionsweise zur Verfügung. Keine öffentlichen Downloads." },
            { title: "Was passiert bei Zahlungsausfall?", body: "Wir suchen zuerst das Gespräch. Bei anhaltendem Ausfall wird der Kurszugang gemäss vertraglicher Regelung eingeschränkt." },
          ]}
        />
      </Section>

      <GoldRule />

      {/* 13. FINAL CTA */}
      <Section id="final-cta">
        <div className="text-center">
          <h2
            className="text-3xl md:text-5xl font-serif mb-8"
            style={{ color: S.ivory }}
            data-testid="final-cta-title"
          >
            Bereit, deinen Weg zu gehen?
          </h2>
          <PrimaryCta
            href="/sat-master/bewerbung"
            label="Meinen Platz anfragen"
            testId="final-cta-button"
          />
        </div>
      </Section>

      <FooterDisclaimer />
    </div>
  );
}
